import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";

import { OutreachAutomationType } from "../../../types";
import { SOURCE_TYPE_LABELS, generateDefaultName } from "../../schema";

/* ── Types ────────────────────────────────────────────────────────── */

interface ConfigureSettingsProps {
  draftId: number;
  sourceType: OutreachAutomationType;
  onActivate: (settings: SettingsPayload) => Promise<void>;
  onEditStep: (step: "configure" | "template") => void;
  initialBrandCount?: number | null;
  initialApprovalMode?: "auto_schedule" | "approval_required" | null;
  initialStartDate?: string | null;
  initialTimezone?: string | null;
  draftName?: string | null;
  draftConfig?: Record<string, any> | null;
  draftTemplateIds?: number[] | null;
}

export interface SettingsPayload {
  name: string;
  brandCount: number;
  approvalMode: "auto_schedule" | "approval_required";
  startDate: string;
  timezone: string;
}

/* ── Helpers ──────────────────────────────────────────────────────── */

function getNextMondays(count: number): { value: string; label: string }[] {
  const mondays: { value: string; label: string }[] = [];
  const today = moment();
  let next = today.clone().startOf("isoWeek").add(1, "week");
  for (let i = 0; i < count; i++) {
    mondays.push({
      value: next.format("YYYY-MM-DD"),
      label: next.format("MMM D, YYYY"),
    });
    next = next.clone().add(1, "week");
  }
  return mondays;
}

function getBrandCountOptions(
  sourceType: OutreachAutomationType,
): { value: number; label: string }[] {
  const max = sourceType === "bento_database" ? 125 : 50;
  const steps =
    sourceType === "bento_database"
      ? [5, 10, 15, 25, 50, 75, 100, 125]
      : [5, 10, 15, 25, 50];
  return steps
    .filter((n) => n <= max)
    .map((n) => ({ value: n, label: `${n}` }));
}

const TIMEZONE_OPTIONS = moment.tz.names();
const MONDAY_OPTIONS = getNextMondays(8);

interface ConfigSummaryRow {
  label: string;
  value: string;
  highlight?: boolean;
  chips?: string[];
}

function getConfigSummaryRows(
  sourceType: OutreachAutomationType,
  config: Record<string, any> | null | undefined,
  brandCount: number,
): ConfigSummaryRow[] {
  const rows: ConfigSummaryRow[] = [];
  rows.push({ label: "Source:", value: SOURCE_TYPE_LABELS[sourceType] });

  if (config) {
    if (sourceType === "google_places") {
      if (config.search_queries?.length) {
        rows.push({
          label: "Search:",
          value: config.search_queries.join(", "),
          highlight: true,
        });
      }
      if (config.geopoints?.length) {
        const locs = config.geopoints.map((g: string) => {
          try {
            const parsed = typeof g === "string" ? JSON.parse(g) : g;
            return parsed.label || parsed.name || "Location";
          } catch {
            return "Location";
          }
        });
        rows.push({ label: "Locations:", value: "", chips: locs });
      }
    } else if (sourceType === "instagram_tracked_creators") {
      if (config.instagram_usernames?.length) {
        rows.push({
          label: "Tracking:",
          value: config.instagram_usernames
            .map((u: string) => `@${u}`)
            .join(", "),
        });
      }
    } else if (sourceType === "bento_database") {
      if (config.search_parameters?.category_ids?.length) {
        rows.push({
          label: "Categories:",
          value: `${config.search_parameters.category_ids.length} selected`,
        });
      }
    }
  }

  rows.push({
    label: "Expected:",
    value: `~${brandCount} new brands per week`,
  });

  return rows;
}

/* ── Shared sx tokens ─────────────────────────────────────────────── */

const inputSx = {
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(1,67,57,0.2)",
    borderWidth: 2,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
};

const cardSx = {
  backgroundColor: "white",
  border: "2px solid rgba(1,67,57,0.2)",
  borderRadius: "14px",
  pt: "26px",
  px: "26px",
  pb: "24px",
};

const cardHeadingSx = {
  fontWeight: 700,
  fontSize: 18,
  color: "primary.main",
  lineHeight: "28px",
};

const labelSx = {
  fontWeight: 600,
  fontSize: 14,
  color: "primary.main",
  lineHeight: "20px",
};

const hintSx = {
  fontSize: 12,
  color: "rgba(1,67,57,0.6)",
  lineHeight: "16px",
};

/* ── Main component ───────────────────────────────────────────────── */

export default function ConfigureSettings({
  draftId: _draftId,
  sourceType,
  onActivate,
  onEditStep,
  initialBrandCount,
  initialApprovalMode,
  initialStartDate,
  initialTimezone,
  draftName,
  draftConfig,
  draftTemplateIds,
}: ConfigureSettingsProps) {
  const { categoriesById } = useContext(BentoCategoriesContext);
  const { templates: allTemplates, fetchOutreachTemplates } = useContext(
    OutreachTemplatesContext,
  );

  const brandCountOptions = getBrandCountOptions(sourceType);

  useEffect(() => {
    if (!allTemplates?.length) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve template IDs to template objects
  const resolvedTemplates = useMemo(() => {
    if (!draftTemplateIds?.length || !allTemplates?.length) return [];
    return draftTemplateIds
      .map((id) => allTemplates.find((t: Template) => t.id === id))
      .filter((t): t is Template => !!t);
  }, [draftTemplateIds, allTemplates]);

  const outreachTemplate = resolvedTemplates.find(
    (t) => t.contentType === TemplateContentType.OUTREACH,
  );
  const followUpTemplates = resolvedTemplates.filter(
    (t) => t.contentType === TemplateContentType.FOLLOWUP,
  );

  const catIds: string[] = draftConfig?.search_parameters?.category_ids || [];
  const catNames = [
    ...new Set(
      catIds
        .map((id: string) => categoriesById[Number(id)]?.name)
        .filter(Boolean),
    ),
  ];
  const [automationName, setAutomationName] = useState<string>(
    draftName || generateDefaultName(sourceType, draftConfig, catNames),
  );
  const [brandCount, setBrandCount] = useState<number>(initialBrandCount ?? 10);
  const [approvalMode, setApprovalMode] = useState<
    "auto_schedule" | "approval_required"
  >(initialApprovalMode ?? "approval_required");
  const [startDate, setStartDate] = useState<string>(
    initialStartDate ?? MONDAY_OPTIONS[0]?.value ?? "",
  );
  const [timezone, setTimezone] = useState<string>(
    initialTimezone ?? moment.tz.guess(),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialBrandCount != null) setBrandCount(initialBrandCount);
    if (initialApprovalMode != null) setApprovalMode(initialApprovalMode);
    if (initialStartDate) setStartDate(initialStartDate);
    if (initialTimezone) setTimezone(initialTimezone);
  }, [
    initialBrandCount,
    initialApprovalMode,
    initialStartDate,
    initialTimezone,
  ]);

  const isFormValid =
    automationName.trim() !== "" &&
    brandCount > 0 &&
    startDate &&
    timezone.length > 0;

  const handleActivate = async () => {
    if (!isFormValid) return;
    setLoading(true);
    try {
      await onActivate({
        name: automationName.trim(),
        brandCount,
        approvalMode,
        startDate,
        timezone,
      });
    } catch {
      setLoading(false);
    }
  };

  const configRows = getConfigSummaryRows(sourceType, draftConfig, brandCount);

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* ── Header ── */}
      <Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 24,
            color: "text.primary",
            lineHeight: "32px",
          }}
        >
          Review & Confirm
        </Typography>
        <Typography
          sx={{ fontSize: 16, color: "text.secondary", lineHeight: "24px" }}
        >
          Review your automation details before launching
        </Typography>
      </Box>

      {/* ── Two-column layout ── */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* ── Left column: stacked cards ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* ── Card 1: What You're Pitching ── */}
          <Box sx={cardSx}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Box
                  component="i"
                  className="fa-solid fa-chart-line"
                  sx={{ fontSize: 16, color: "primary.main" }}
                />
                <Typography sx={cardHeadingSx}>What You're Pitching</Typography>
              </Box>
              <Box
                onClick={() => onEditStep("configure")}
                sx={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <Box
                  component="i"
                  className="fa-solid fa-arrow-left"
                  sx={{ fontSize: 12, color: "primary.main" }}
                />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: "primary.main",
                    lineHeight: "20px",
                  }}
                >
                  Edit
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {configRows.map((row, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "rgba(1,67,57,0.7)",
                      lineHeight: "20px",
                      width: 128,
                      flexShrink: 0,
                      pt: row.highlight ? "8px" : 0,
                    }}
                  >
                    {row.label}
                  </Typography>
                  {row.chips ? (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      {row.chips.map((chip, ci) => (
                        <Chip
                          key={ci}
                          label={chip}
                          size="small"
                          icon={
                            <Box
                              component="i"
                              className="fa-solid fa-location-dot"
                              sx={{ fontSize: 11 }}
                            />
                          }
                          sx={{
                            backgroundColor: "rgba(1,67,57,0.08)",
                            color: "primary.main",
                            fontWeight: 500,
                            fontSize: 13,
                            "& .MuiChip-icon": { color: "primary.main" },
                          }}
                        />
                      ))}
                    </Box>
                  ) : row.highlight ? (
                    <Box
                      sx={{
                        flex: 1,
                        backgroundColor: "rgba(247,238,111,0.2)",
                        borderRadius: "10px",
                        px: 1.5,
                        py: "6px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          color: "primary.main",
                          lineHeight: "24px",
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: row.label === "Source:" ? 500 : 400,
                        fontSize: 16,
                        color: "primary.main",
                        lineHeight: "24px",
                        flex: 1,
                      }}
                    >
                      {row.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Card 2: Email Template ── */}
          <Box
            sx={{
              ...cardSx,
              borderColor: "rgba(177,179,248,0.3)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Box
                  component="i"
                  className="fa-regular fa-envelope"
                  sx={{ fontSize: 16, color: "primary.main" }}
                />
                <Typography sx={cardHeadingSx}>Email Template</Typography>
              </Box>
              <Box
                onClick={() => onEditStep("template")}
                sx={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <Box
                  component="i"
                  className="fa-solid fa-arrow-left"
                  sx={{ fontSize: 12, color: "primary.main" }}
                />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: "primary.main",
                    lineHeight: "20px",
                  }}
                >
                  Edit
                </Typography>
              </Box>
            </Box>

            {!allTemplates?.length && draftTemplateIds?.length ? (
              <Skeleton
                variant="rounded"
                height={120}
                sx={{ borderRadius: "14px" }}
              />
            ) : outreachTemplate ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    backgroundColor: "rgba(177,179,248,0.1)",
                    borderRadius: "10px",
                    px: 2,
                    py: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 16,
                      color: "primary.main",
                      lineHeight: "24px",
                      mb: 0.5,
                    }}
                  >
                    {outreachTemplate.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "rgba(1,67,57,0.7)",
                      lineHeight: "20px",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 600, fontSize: 14 }}
                    >
                      Subject:
                    </Typography>{" "}
                    {outreachTemplate.subject}
                  </Typography>
                </Box>

                {followUpTemplates.map((ft, i) => (
                  <Box
                    key={ft.id ?? i}
                    sx={{
                      backgroundColor: "rgba(177,179,248,0.1)",
                      borderRadius: "10px",
                      px: 2,
                      py: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: "primary.main",
                        lineHeight: "24px",
                        mb: 0.5,
                      }}
                    >
                      Follow-up {i + 1}: {ft.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "rgba(1,67,57,0.7)",
                        lineHeight: "20px",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 600, fontSize: 14 }}
                      >
                        Subject:
                      </Typography>{" "}
                      {ft.subject}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 14, color: "rgba(1,67,57,0.5)" }}>
                No templates selected
              </Typography>
            )}
          </Box>

          {/* ── Card 3: Automation Settings ── */}
          <Box sx={cardSx}>
            <Box
              sx={{ display: "flex", gap: "8px", alignItems: "center", mb: 2 }}
            >
              <Box
                component="i"
                className="fa-solid fa-gear"
                sx={{ fontSize: 16, color: "primary.main" }}
              />
              <Typography sx={cardHeadingSx}>Automation Settings</Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Automation Name */}
              <Box>
                <Typography sx={{ ...labelSx, mb: "8px" }}>
                  Automation Name
                </Typography>
                <TextField
                  fullWidth
                  value={automationName}
                  onChange={(e) => setAutomationName(e.target.value)}
                  placeholder="Enter a name for this automation"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "& fieldset": {
                        borderColor: "rgba(1,67,57,0.2)",
                        borderWidth: 2,
                      },
                      "&:hover fieldset": { borderColor: "primary.main" },
                      "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    },
                  }}
                />
                <Typography sx={{ ...hintSx, mt: 0.5 }}>
                  A name to help you identify this automation
                </Typography>
              </Box>

              {/* Automation Frequency (fixed) */}
              <Box>
                <Typography sx={{ ...labelSx, mb: "8px" }}>
                  Automation Frequency
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "rgba(247,238,111,0.1)",
                    border: "2px solid rgba(247,238,111,0.3)",
                    borderRadius: "10px",
                    px: "18px",
                    py: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: 16,
                      color: "primary.main",
                    }}
                  >
                    Weekly
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "rgba(247,238,111,0.2)",
                      borderRadius: "4px",
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)" }}
                    >
                      Fixed
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ ...hintSx, mt: 0.5 }}>
                  We'll check for new brands and send pitches every week
                </Typography>
              </Box>

              {/* Target Pitches Per Week */}
              <Box>
                <Typography sx={{ ...labelSx, mb: "8px" }}>
                  Target Pitches Per Week
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  <Select
                    value={brandCount}
                    onChange={(e) => setBrandCount(Number(e.target.value))}
                    size="small"
                    sx={{ ...inputSx, flex: 1 }}
                  >
                    {brandCountOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "rgba(1,67,57,0.7)",
                      flexShrink: 0,
                    }}
                  >
                    pitches/week
                  </Typography>
                </Box>
                <Typography sx={{ ...hintSx, mt: 0.5 }}>
                  Maximum: {sourceType === "bento_database" ? "125" : "50"}{" "}
                  pitches per week per automation
                </Typography>
              </Box>

              {/* Start Pitching On */}
              <Box>
                <Typography sx={{ ...labelSx, mb: "8px" }}>
                  Start Pitching On
                </Typography>
                <Select
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value as string)}
                  size="small"
                  fullWidth
                  sx={inputSx}
                >
                  {MONDAY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      Monday, {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Sending Mode */}
              <Box>
                <Typography sx={{ ...labelSx, mb: "8px" }}>
                  Sending Mode
                </Typography>

                {/* Auto-send option */}
                <Box
                  onClick={() => setApprovalMode("auto_schedule")}
                  sx={{
                    border: "2px solid",
                    borderColor:
                      approvalMode === "auto_schedule"
                        ? "primary.main"
                        : "rgba(1,67,57,0.2)",
                    borderRadius: "10px",
                    px: "18px",
                    py: "14px",
                    mb: 1.5,
                    cursor: "pointer",
                    backgroundColor:
                      approvalMode === "auto_schedule"
                        ? "rgba(1,67,57,0.03)"
                        : "white",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor:
                          approvalMode === "auto_schedule"
                            ? "primary.main"
                            : "rgba(1,67,57,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {approvalMode === "auto_schedule" && (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "primary.main",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "primary.main",
                        }}
                      >
                        Auto-send pitches
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)" }}
                      >
                        Pitches are automatically sent during business hours
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Review option */}
                <Box
                  onClick={() => setApprovalMode("approval_required")}
                  sx={{
                    border: "2px solid",
                    borderColor:
                      approvalMode === "approval_required"
                        ? "primary.main"
                        : "rgba(1,67,57,0.2)",
                    borderRadius: "10px",
                    px: "18px",
                    py: "14px",
                    cursor: "pointer",
                    backgroundColor:
                      approvalMode === "approval_required"
                        ? "rgba(1,67,57,0.03)"
                        : "white",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor:
                          approvalMode === "approval_required"
                            ? "primary.main"
                            : "rgba(1,67,57,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {approvalMode === "approval_required" && (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "primary.main",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "primary.main",
                        }}
                      >
                        Review before sending
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)" }}
                      >
                        Pitches are queued for your approval before being sent
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Divider + Sending Schedule */}
              <Box
                sx={{
                  borderTop: "1px solid rgba(1,67,57,0.1)",
                  pt: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <Typography sx={labelSx}>Sending Schedule</Typography>

                {/* Timezone */}
                <Box>
                  <Typography sx={{ ...labelSx, mb: "8px" }}>
                    Timezone
                  </Typography>
                  <Autocomplete
                    options={TIMEZONE_OPTIONS}
                    value={timezone}
                    onChange={(_e, newValue) => {
                      if (newValue) setTimezone(newValue);
                    }}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select timezone"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: "rgba(1,67,57,0.2)",
                              borderWidth: 2,
                            },
                            "&:hover fieldset": { borderColor: "primary.main" },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Business Hours Info */}
                <Box
                  sx={{
                    backgroundColor: "rgba(1,67,57,0.03)",
                    border: "1px solid rgba(1,67,57,0.1)",
                    borderRadius: "10px",
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-clock"
                      sx={{ fontSize: 14, color: "rgba(1,67,57,0.6)" }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "primary.main",
                      }}
                    >
                      Sending Schedule
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <Box
                      component="li"
                      sx={{ color: "rgba(1,67,57,0.5)", mb: 0.5 }}
                    >
                      <Typography sx={hintSx}>
                        Emails are only sent during business hours (9 AM – 5 PM)
                      </Typography>
                    </Box>
                    <Box
                      component="li"
                      sx={{ color: "rgba(1,67,57,0.5)", mb: 0.5 }}
                    >
                      <Typography sx={hintSx}>
                        No emails are sent on weekends
                      </Typography>
                    </Box>
                    <Box component="li" sx={{ color: "rgba(1,67,57,0.5)" }}>
                      <Typography sx={hintSx}>
                        Times are based on the timezone selected above
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Activate button ── */}
          <LoadingButton
            variant="contained"
            fullWidth
            disabled={!isFormValid}
            loading={loading}
            onClick={handleActivate}
            sx={{
              borderRadius: "10px",
              backgroundColor: "primary.main",
              fontWeight: 600,
              fontSize: 16,
              py: 1.5,
              textTransform: "none",
            }}
          >
            Save Automation
          </LoadingButton>
        </Box>
        {/* end left column */}

        {/* ── Right column: tips ── */}
        <Box
          sx={{
            width: 310,
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Tips card */}
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fee685",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}
            >
              <Box
                component="span"
                className="fa-solid fa-lightbulb"
                sx={{ fontSize: 18, color: "#7b3306" }}
              />
              <Typography
                sx={{ fontWeight: 600, fontSize: 18, color: "#7b3306" }}
              >
                Tips
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {[
                "Start with a smaller number of pitches and scale up as you refine your templates",
                "Review mode is recommended when starting a new automation",
                "Emails are spaced throughout business hours for natural delivery",
                "You can pause or adjust the automation at any time after activation",
              ].map((tip, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    color: "#e17100",
                    mb: 1,
                    "&::marker": { color: "#e17100" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#7b3306",
                      lineHeight: "20px",
                    }}
                  >
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* How it works card */}
          <Box
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#166534", mb: 1 }}
            >
              How it works
            </Typography>
            <Box sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}>
              {[
                "Automation finds new brands matching your criteria each week",
                "Outreach emails are sent during business hours in your timezone",
                "Follow-ups are sent automatically if no reply is received",
              ].map((text, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", gap: 1, mb: i < 2 ? 0.75 : 0 }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                      borderRadius: "4px",
                      px: 0.75,
                      py: 0.25,
                      lineHeight: "18px",
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#15803d" }}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      {/* end two-column layout */}
    </Box>
  );
}
